import { useEffect, useRef, useState, useCallback } from 'react';
import { crewCertificatesService, employeeDocumentsService } from '../../services';
import { CrewCertificate } from '../../types';
import { resolveDocumentExpiry } from './documentHelpers';

export interface CrewDocumentFilters {
  search: string;
  courseType: string;
  profession: string;
  status: string;
  rank: string;
  expiry: string;
  dateFrom: string;
  dateTo: string;
}

const isoMonthsAhead = (months: number): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
};

/** Matches getCertificateExpiryStatus: expired / expiring within 3 months / valid. */
const expiryCondition = (expiry: string): any | null => {
  const today = isoMonthsAhead(0);
  const soon = isoMonthsAhead(3);
  if (expiry === 'expired') return { expiryDate: { $lt: today } };
  if (expiry === 'expiring_soon') return { expiryDate: { $gte: today, $lte: soon } };
  if (expiry === 'valid') return { $or: [{ expiryDate: { $gt: soon } }, { expiryDate: { $null: true } }] };
  return null;
};

/** Server-side filters for crew certificates (search covers the whole dataset). */
const buildCertificateFilters = (f: CrewDocumentFilters): any | undefined => {
  const and: any[] = [];
  if (f.search) {
    and.push({ $or: [
      { courseType: { $containsi: f.search } },
      { certificateNumber: { $containsi: f.search } },
      { profession: { $containsi: f.search } },
      { seamanBookNumber: { $containsi: f.search } },
      { employee: { name: { $containsi: f.search } } },
      { employee: { arabicName: { $containsi: f.search } } },
      { employee: { code: { $containsi: f.search } } },
    ] });
  }
  if (f.courseType !== 'all') and.push({ courseType: { $eq: f.courseType } });
  if (f.profession !== 'all') {
    and.push({ $or: [{ profession: { $eq: f.profession } }, { profession: { $null: true } }] });
  }
  if (f.status !== 'all') and.push({ crew_certificate_status: { $eq: f.status } });
  if (f.rank !== 'all') and.push({ employee: { rank: { $eq: f.rank } } });
  const expiry = expiryCondition(f.expiry);
  if (expiry) and.push(expiry);
  if (f.dateFrom) and.push({ issueDate: { $gte: f.dateFrom } });
  if (f.dateTo) and.push({ issueDate: { $lte: f.dateTo } });
  return and.length ? { $and: and } : undefined;
};

/** Same filters translated for uploaded employee documents (IDs, passports...). */
const buildUploadedDocFilters = (f: CrewDocumentFilters): any | undefined => {
  const and: any[] = [];
  if (f.search) {
    and.push({ $or: [
      { documentType: { $containsi: f.search } },
      { documentNumber: { $containsi: f.search } },
      { employee: { name: { $containsi: f.search } } },
      { employee: { arabicName: { $containsi: f.search } } },
      { employee: { code: { $containsi: f.search } } },
    ] });
  }
  if (f.courseType !== 'all') and.push({ documentType: { $eq: f.courseType } });
  if (f.status !== 'all') and.push({ employee_document_status: { $eq: f.status } });
  if (f.rank !== 'all') and.push({ employee: { rank: { $eq: f.rank } } });
  const expiry = expiryCondition(f.expiry);
  if (expiry) and.push(expiry);
  if (f.dateFrom) and.push({ issueDate: { $gte: f.dateFrom } });
  if (f.dateTo) and.push({ issueDate: { $lte: f.dateTo } });
  return and.length ? { $and: and } : undefined;
};

const mapUploadedDoc = (d: any): CrewCertificate => ({
  ...d,
  expiryDate: resolveDocumentExpiry(d.documentType, d, d.employee) || d.expiryDate,
  employeeId: d.employeeId || d.employee?.documentId || d.employee,
  courseType: d.documentType,
  certificateNumber: d.documentNumber || '',
  isUploadedDocument: true,
} as any);

// Certificates with a missing expiryDate recover it from the linked
// employee when the course type identifies the document (seaman's book...)
const mapCertificate = (c: any): CrewCertificate => ({
  ...c,
  expiryDate: resolveDocumentExpiry(c.courseType, c, c.employee) || c.expiryDate,
} as any);

const SORT = [{ field: 'createdAt', order: 'desc' as const }];

const appendCondition = (filters: any | undefined, condition: any): any => {
  if (!filters) return { $and: [condition] };
  return { $and: [...filters.$and, condition] };
};

const countMatching = async (filters: any | undefined, condition: any): Promise<number> => {
  const response: any = await crewCertificatesService.getAll({
    filters: appendCondition(filters, condition),
    pagination: { page: 1, pageSize: 1 },
  });
  return response.meta?.pagination?.total ?? 0;
};

/**
 * One server-paginated list over two collections: uploaded employee documents
 * come first (there are few), then crew certificates. Certificates are fetched
 * page-by-page from the server with all filters applied there, so search and
 * pagination always cover the whole dataset.
 */
export function useCrewDocumentsPage(page: number, pageSize: number, filters: CrewDocumentFilters) {
  const [rows, setRows] = useState<CrewCertificate[]>([]);
  const [total, setTotal] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const [expiringSoonCount, setExpiringSoonCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [version, setVersion] = useState(0);
  const uploadedCache = useRef<{ key: string; docs: CrewCertificate[] } | null>(null);

  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    let active = true;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const parsedFilters: CrewDocumentFilters = JSON.parse(filterKey);
        const certFilters = buildCertificateFilters(parsedFilters);
        const docFilters = buildUploadedDocFilters(parsedFilters);

        const docKey = `${JSON.stringify(docFilters || {})}#${version}`;
        let uploaded: CrewCertificate[];
        if (uploadedCache.current?.key === docKey) {
          uploaded = uploadedCache.current.docs;
        } else {
          const res: any = await employeeDocumentsService.getAll({
            ...(docFilters ? { filters: docFilters } : {}),
            sort: SORT,
          });
          uploaded = (res.data || []).map(mapUploadedDoc);
          uploadedCache.current = { key: docKey, docs: uploaded };
        }

        const offset = (page - 1) * pageSize;
        const fromUploaded = uploaded.slice(offset, offset + pageSize);
        const needed = pageSize - fromUploaded.length;
        const certOffset = Math.max(0, offset - uploaded.length);
        const certPage = Math.floor(certOffset / pageSize) + 1;
        const baseOptions = { ...(certFilters ? { filters: certFilters } : {}), sort: SORT };

        const r1: any = await crewCertificatesService.getAll({
          ...baseOptions,
          pagination: { page: certPage, pageSize },
        });
        const certTotal = r1.meta?.pagination?.total ?? (r1.data || []).length;
        let certs: CrewCertificate[] = [];
        if (needed > 0) {
          const start = certOffset - (certPage - 1) * pageSize;
          certs = (r1.data || []).slice(start, start + needed).map(mapCertificate);
          if (certs.length < needed && certPage < (r1.meta?.pagination?.pageCount || 1)) {
            const r2: any = await crewCertificatesService.getAll({
              ...baseOptions,
              pagination: { page: certPage + 1, pageSize },
            });
            certs = certs.concat((r2.data || []).slice(0, needed - certs.length).map(mapCertificate));
          }
        }

        // Badge counts over the whole filtered dataset, not just the page
        const [expiredCerts, expiringCerts] = await Promise.all([
          countMatching(certFilters, expiryCondition('expired')),
          countMatching(certFilters, expiryCondition('expiring_soon')),
        ]);
        const today = isoMonthsAhead(0);
        const soon = isoMonthsAhead(3);
        const expiredDocs = uploaded.filter(d => d.expiryDate && d.expiryDate < today).length;
        const expiringDocs = uploaded.filter(d => d.expiryDate && d.expiryDate >= today && d.expiryDate <= soon).length;

        if (!active) return;
        setRows([...fromUploaded, ...certs]);
        setTotal(uploaded.length + certTotal);
        setExpiredCount(expiredCerts + expiredDocs);
        setExpiringSoonCount(expiringCerts + expiringDocs);
        setHasLoaded(true);
      } catch (e: any) {
        if (active) setError(e instanceof Error ? e : new Error('Failed to load documents'));
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, pageSize, filterKey, version]);

  const refetch = useCallback(() => setVersion(v => v + 1), []);

  /** Every filtered record from both collections — used by export and print. */
  const fetchAllFiltered = useCallback(async (): Promise<CrewCertificate[]> => {
    const parsedFilters: CrewDocumentFilters = JSON.parse(filterKey);
    const certFilters = buildCertificateFilters(parsedFilters);
    const docFilters = buildUploadedDocFilters(parsedFilters);
    const [docsRes, certsRes]: any[] = await Promise.all([
      employeeDocumentsService.getAll({ ...(docFilters ? { filters: docFilters } : {}), sort: SORT }),
      crewCertificatesService.getAll({ ...(certFilters ? { filters: certFilters } : {}), sort: SORT }),
    ]);
    return [...(docsRes.data || []).map(mapUploadedDoc), ...(certsRes.data || []).map(mapCertificate)];
  }, [filterKey]);

  return { rows, total, expiredCount, expiringSoonCount, isLoading, hasLoaded, error, refetch, fetchAllFiltered };
}
